import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { 
  projectsApi, 
  vendorsApi, 
  evaluationsApi, 
  tasksApi,
  communicationsApi,
  auditApi,
  usersApi,
  type Project,
  type VendorResponse,
  type Evaluation,
  type Task,
  type User,
  type ProjectSearchRequest,
  type PaginatedResponse
} from './api'

// Generic pagination hook
export const usePagination = (initialPage: number = 0, initialLimit: number = 10) => {
  const [page, setPage] = useState(initialPage)
  const [limit, setLimit] = useState(initialLimit)
  const [total, setTotal] = useState(0)

  const totalPages = Math.ceil(total / limit)
  const hasNextPage = page < totalPages - 1
  const hasPrevPage = page > 0

  const nextPage = useCallback(() => {
    if (hasNextPage) setPage(prev => prev + 1)
  }, [hasNextPage])

  const prevPage = useCallback(() => {
    if (hasPrevPage) setPage(prev => prev - 1)
  }, [hasPrevPage])

  const goToPage = useCallback((newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPage(newPage)
    }
  }, [totalPages])

  const reset = useCallback(() => {
    setPage(0)
    setTotal(0)
  }, [])

  return {
    page,
    limit,
    total,
    totalPages,
    hasNextPage,
    hasPrevPage,
    setPage,
    setLimit,
    setTotal,
    nextPage,
    prevPage,
    goToPage,
    reset,
    skip: page * limit,
  }
}

// Projects hooks
export const useProjects = (params?: ProjectSearchRequest & { skip?: number; limit?: number }) => {
  return useQuery(['projects', params], () => projectsApi.list(params), {
    keepPreviousData: true,
  })
}

export const useProject = (id: string | undefined) => {
  return useQuery(['project', id], () => projectsApi.get(id!), {
    enabled: !!id,
  })
}

export const useCreateProject = () => {
  const queryClient = useQueryClient()
  
  return useMutation(projectsApi.create, {
    onSuccess: () => {
      // Invalidate all projects queries (including search variants)
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('Project created successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create project')
    },
  })
}

export const useUpdateProject = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ id, data }: { id: string; data: any }) => projectsApi.update(id, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ['projects'] })
        queryClient.invalidateQueries({ queryKey: ['project', data.id] })
        toast.success('Project updated successfully')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update project')
      },
    }
  )
}

export const useDeleteProject = () => {
  const queryClient = useQueryClient()
  
  return useMutation(projectsApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('Project deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete project')
    },
  })
}

export const useProjectRequirements = (projectId: string | undefined) => {
  return useQuery(
    ['project-requirements', projectId],
    () => projectsApi.getRequirements(projectId!),
    {
      enabled: !!projectId,
    }
  )
}

export const useExtractRequirements = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ projectId, force }: { projectId: string; force?: boolean }) =>
      projectsApi.extractRequirements(projectId, force),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['project', variables.projectId])
        queryClient.invalidateQueries(['project-requirements', variables.projectId])
        toast.success('Requirements extraction started')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to extract requirements')
      },
    }
  )
}

// Vendor responses hooks
export const useVendorResponses = (projectId: string | undefined, params?: { skip?: number; limit?: number }) => {
  return useQuery(
    ['vendor-responses', projectId, params],
    () => vendorsApi.list(projectId!, params),
    {
      enabled: !!projectId,
      keepPreviousData: true,
    }
  )
}

export const useVendorResponse = (id: string | undefined) => {
  return useQuery(['vendor-response', id], () => vendorsApi.get(id!), {
    enabled: !!id,
  })
}

export const useCreateVendorResponse = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ projectId, data }: { projectId: string; data: any }) =>
      vendorsApi.create(projectId, data),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['vendor-responses', variables.projectId])
        toast.success('Vendor response created successfully')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to create vendor response')
      },
    }
  )
}


// Evaluations hooks
export const useEvaluations = (params?: { skip?: number; limit?: number }) => {
  return useQuery(['evaluations', params], () => evaluationsApi.list(params), {
    keepPreviousData: true,
  })
}

export const useResponseEvaluations = (responseId: string | undefined) => {
  return useQuery(
    ['response-evaluations', responseId],
    () => evaluationsApi.getResponseEvaluations(responseId!),
    {
      enabled: !!responseId,
    }
  )
}

export const useProjectEvaluations = (projectId: string | undefined, params?: { skip?: number; limit?: number }) => {
  return useQuery(
    ['project-evaluations', projectId, params],
    () => evaluationsApi.getProjectEvaluations(projectId!, params),
    {
      enabled: !!projectId,
      keepPreviousData: true,
    }
  )
}

export const useEvaluateResponse = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ responseId, force }: { responseId: string; force?: boolean }) =>
      evaluationsApi.evaluateResponse(responseId, { force_reevaluation: force }),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['response-evaluations', variables.responseId])
        queryClient.invalidateQueries(['vendor-response', variables.responseId])
        toast.success('Response evaluation started')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to evaluate response')
      },
    }
  )
}

export const useUpdateEvaluation = () => {
  const queryClient = useQueryClient()
  
  return useMutation(
    ({ evaluationId, data }: { evaluationId: string; data: Partial<import('./api').EvaluationCreateRequest> }) =>
      evaluationsApi.update(evaluationId, data),
    {
      onSuccess: (data, variables) => {
        // Invalidate relevant queries to refresh the UI
        if (data.vendor_response?.id) {
          queryClient.invalidateQueries(['response-evaluations', data.vendor_response.id])
          queryClient.invalidateQueries(['vendor-response', data.vendor_response.id])
        }
        queryClient.invalidateQueries(['evaluations'])
        toast.success('Evaluation updated successfully')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update evaluation')
      },
    }
  )
}

// Tasks hooks
export const useTask = (taskId: string | undefined) => {
  return useQuery(['task', taskId], () => tasksApi.getStatus(taskId!), {
    enabled: !!taskId,
    refetchInterval: (data) => {
      // Refetch every 2 seconds if task is still running
      if (data?.status === 'running' || data?.status === 'pending') {
        return 2000
      }
      return false
    },
  })
}

export const useTaskQueue = () => {
  return useQuery(['task-queue'], tasksApi.getQueue, {
    refetchInterval: 5000, // Refetch every 5 seconds
  })
}

export const useRunningTasks = () => {
  return useQuery(['running-tasks'], tasksApi.getRunning, {
    refetchInterval: 3000, // Refetch every 3 seconds
  })
}

export const useCancelTask = () => {
  const queryClient = useQueryClient()
  
  return useMutation(tasksApi.cancel, {
    onSuccess: () => {
      queryClient.invalidateQueries(['task-queue'])
      queryClient.invalidateQueries(['running-tasks'])
      toast.success('Task cancelled successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to cancel task')
    },
  })
}

// Communications hooks
export const useMessages = (params?: { message_type?: string; unread_only?: boolean; limit?: number }) => {
  return useQuery(['messages', params], () => communicationsApi.getMessages(params))
}

export const useUnreadCount = () => {
  return useQuery(['unread-count'], communicationsApi.getUnreadCount, {
    refetchInterval: 30000, // Refetch every 30 seconds
  })
}

export const useSendMessage = () => {
  const queryClient = useQueryClient()
  
  return useMutation(communicationsApi.sendMessage, {
    onSuccess: () => {
      queryClient.invalidateQueries(['messages'])
      toast.success('Message sent successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to send message')
    },
  })
}

// Users hooks
export const useUsers = (params?: { skip?: number; limit?: number }) => {
  return useQuery(['users', params], () => usersApi.list(params), {
    keepPreviousData: true,
  })
}

export const useUser = (id: string | undefined) => {
  return useQuery(['user', id], () => usersApi.get(id!), {
    enabled: !!id,
  })
}

// File upload hook with progress
export const useFileUpload = () => {
  const [progress, setProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)

  const upload = useCallback(async (file: File, uploadFn: (formData: FormData, onProgress: (p: number) => void) => Promise<any>) => {
    const formData = new FormData()
    formData.append('file', file)

    setIsUploading(true)
    setProgress(0)

    try {
      const result = await uploadFn(formData, setProgress)
      setProgress(100)
      return result
    } finally {
      setIsUploading(false)
    }
  }, [])

  return {
    upload,
    progress,
    isUploading,
  }
}

// Debounced search hook
export const useDebouncedSearch = (delay: number = 300) => {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const timeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      setDebouncedQuery(query)
    }, delay)

    return () => clearTimeout(timeoutRef.current)
  }, [query, delay])

  return {
    query,
    debouncedQuery,
    setQuery,
  }
}

// Local storage hook
export const useLocalStorage = <T>(key: string, initialValue: T) => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      return initialValue
    }
  })

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value
      setStoredValue(valueToStore)
      window.localStorage.setItem(key, JSON.stringify(valueToStore))
    } catch (error) {
      console.error('Error saving to localStorage:', error)
    }
  }

  return [storedValue, setValue] as const
}

// Intersection observer hook
export const useIntersectionObserver = (
  ref: React.RefObject<Element>,
  options: IntersectionObserverInit = {}
) => {
  const [isIntersecting, setIsIntersecting] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting)
      },
      options
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [ref, options])

  return isIntersecting
}

// Copy to clipboard hook
export const useCopyToClipboard = () => {
  const [copiedText, setCopiedText] = useState<string | null>(null)

  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedText(text)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopiedText(null), 2000)
      return true
    } catch (error) {
      toast.error('Failed to copy to clipboard')
      return false
    }
  }, [])

  return { copy, copiedText }
}

// Previous value hook
export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>()
  
  useEffect(() => {
    ref.current = value
  })
  
  return ref.current
}

// Toggle hook
export const useToggle = (initialValue: boolean = false) => {
  const [value, setValue] = useState(initialValue)
  
  const toggle = useCallback(() => setValue(prev => !prev), [])
  const setTrue = useCallback(() => setValue(true), [])
  const setFalse = useCallback(() => setValue(false), [])
  
  return [value, toggle, setTrue, setFalse] as const
}